import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../../services/api';
import type {
  BiDashboard,
  BiWidget,
  DashboardShare,
  EmbedToken,
  ReportSchedule,
  DataAlert,
} from '../../../types/bi';

/* ─── Dashboard Queries ────────────────────────────────────────────────────── */

export function useDashboardList() {
  return useQuery<{ data: (BiDashboard & { user_permission: string })[]; total: number }>({
    queryKey: ['bi', 'dashboards'],
    queryFn: () => api.get('/api/v1/bi/dashboards').then((r) => r.data),
  });
}

export function useDashboard(idOrSlug: string | undefined) {
  return useQuery<BiDashboard & { widgets: BiWidget[]; user_permission?: string }>({
    queryKey: ['bi', 'dashboards', idOrSlug],
    queryFn: () => api.get(`/api/v1/bi/dashboards/${idOrSlug}`).then((r) => r.data),
    enabled: !!idOrSlug,
  });
}

/* ─── Dashboard Mutations ──────────────────────────────────────────────────── */

export function useCreateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string }) =>
      api.post('/api/v1/bi/dashboards', body).then((r) => r.data as BiDashboard),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'dashboards'] }),
  });
}

export function useUpdateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Partial<BiDashboard>) =>
      api.put(`/api/v1/bi/dashboards/${id}`, body).then((r) => r.data as BiDashboard),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['bi', 'dashboards'] });
      qc.invalidateQueries({ queryKey: ['bi', 'dashboards', String(vars.id)] });
    },
  });
}

export function useDeleteDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permanent }: { id: number; permanent?: boolean }) =>
      api
        .delete(`/api/v1/bi/dashboards/${id}${permanent ? '?permanent=true' : ''}`)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'dashboards'] }),
  });
}

export function useCloneDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name?: string }) =>
      api.post(`/api/v1/bi/dashboards/${id}/clone`, { name }).then((r) => r.data as BiDashboard),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'dashboards'] }),
  });
}

/* ─── Widget Mutations ─────────────────────────────────────────────────────── */

export function useAddWidget(dashboardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      widget_type: string;
      title?: string;
      data_config?: unknown;
      semantic_intent?: unknown;
      style_config?: unknown;
    }) =>
      api
        .post(`/api/v1/bi/dashboards/${dashboardId}/widgets`, body)
        .then((r) => r.data as BiWidget),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'dashboards'], exact: false }),
  });
}

export function useUpdateWidget(dashboardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ widgetId, ...body }: { widgetId: number } & Partial<BiWidget>) =>
      api
        .put(`/api/v1/bi/dashboards/${dashboardId}/widgets/${widgetId}`, body)
        .then((r) => r.data as BiWidget),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bi', 'dashboards'], exact: false });
      qc.invalidateQueries({ queryKey: ['bi', 'query'], exact: false });
    },
  });
}

export function useRemoveWidget(dashboardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (widgetId: number) =>
      api.delete(`/api/v1/bi/dashboards/${dashboardId}/widgets/${widgetId}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'dashboards'], exact: false }),
  });
}

export function useBatchUpdatePositions(dashboardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (positions: Array<{ id: number; x: number; y: number; w: number; h: number }>) =>
      api
        .put(`/api/v1/bi/dashboards/${dashboardId}/widgets/positions`, { positions })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'dashboards'], exact: false }),
  });
}

/* ─── Share Queries & Mutations ────────────────────────────────────────────── */

export function useShareList(dashboardId: number) {
  return useQuery<{ data: DashboardShare[] }>({
    queryKey: ['bi', 'shares', dashboardId],
    queryFn: () => api.get(`/api/v1/bi/dashboards/${dashboardId}/shares`).then((r) => r.data),
    enabled: dashboardId > 0,
  });
}

export function useCreateShare(dashboardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { shared_with: string; permission?: 'view' | 'edit' }) =>
      api
        .post(`/api/v1/bi/dashboards/${dashboardId}/shares`, body)
        .then((r) => r.data as DashboardShare),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'shares', dashboardId] }),
  });
}

export function useDeleteShare(dashboardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shareId: number) =>
      api.delete(`/api/v1/bi/dashboards/${dashboardId}/shares/${shareId}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'shares', dashboardId] }),
  });
}

/* ─── Embed Token Queries & Mutations ──────────────────────────────────────── */

export function useEmbedTokenList(dashboardId: number) {
  return useQuery<{ data: EmbedToken[] }>({
    queryKey: ['bi', 'embeds', dashboardId],
    queryFn: () => api.get(`/api/v1/bi/dashboards/${dashboardId}/embeds`).then((r) => r.data),
    enabled: dashboardId > 0,
  });
}

export function useCreateEmbedToken(dashboardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { label?: string; filters_lock?: unknown[]; expires_at?: string }) =>
      api
        .post(`/api/v1/bi/dashboards/${dashboardId}/embeds`, body)
        .then((r) => r.data as EmbedToken & { token: string }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'embeds', dashboardId] }),
  });
}

export function useDeleteEmbedToken(dashboardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: number) =>
      api.delete(`/api/v1/bi/dashboards/${dashboardId}/embeds/${tokenId}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'embeds', dashboardId] }),
  });
}

/* ─── Public Embed Query (no auth) ─────────────────────────────────────────── */

export function useEmbedDashboard(token: string | undefined) {
  return useQuery<{
    dashboard_id: number;
    name: string;
    slug: string;
    layout_config: unknown;
    global_filters: unknown;
    date_range: unknown;
    theme_overrides: unknown;
    filters_lock: unknown[];
    widgets: BiWidget[];
  }>({
    queryKey: ['bi', 'embed', token],
    queryFn: () => api.get(`/api/v1/bi/embed/${token}`).then((r) => r.data),
    enabled: !!token,
    retry: false,
  });
}

/* ─── Export ────────────────────────────────────────────────────────────────── */

export function useExportDashboard() {
  return useMutation({
    mutationFn: ({ dashboardId, format }: { dashboardId: number; format: 'pdf' | 'png' | 'csv' }) =>
      api
        .post(`/api/v1/bi/dashboards/${dashboardId}/export`, { format }, { responseType: 'blob' })
        .then((r) => {
          const contentType = String(r.headers['content-type'] || '');
          const ext = format === 'csv' && contentType.includes('zip') ? 'zip' : format;
          return { blob: r.data as Blob, ext };
        }),
  });
}

/* ─── Report Schedules ─────────────────────────────────────────────────────── */

export function useScheduleList(dashboardId: number) {
  return useQuery<{ data: ReportSchedule[] }>({
    queryKey: ['bi', 'schedules', dashboardId],
    queryFn: () => api.get(`/api/v1/bi/dashboards/${dashboardId}/schedules`).then((r) => r.data),
    enabled: dashboardId > 0,
  });
}

export function useCreateSchedule(dashboardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      cron_expression: string;
      recipients: Array<{ email: string }>;
      format: 'pdf' | 'png' | 'csv';
    }) =>
      api
        .post(`/api/v1/bi/dashboards/${dashboardId}/schedules`, body)
        .then((r) => r.data as ReportSchedule),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'schedules', dashboardId] }),
  });
}

export function useUpdateSchedule(dashboardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, ...body }: { scheduleId: number } & Partial<ReportSchedule>) =>
      api
        .put(`/api/v1/bi/dashboards/${dashboardId}/schedules/${scheduleId}`, body)
        .then((r) => r.data as ReportSchedule),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'schedules', dashboardId] }),
  });
}

export function useDeleteSchedule(dashboardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: number) =>
      api
        .delete(`/api/v1/bi/dashboards/${dashboardId}/schedules/${scheduleId}`)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'schedules', dashboardId] }),
  });
}

/* ─── Data Alerts ──────────────────────────────────────────────────────────── */

export function useAlertList() {
  return useQuery<{ data: DataAlert[] }>({
    queryKey: ['bi', 'alerts'],
    queryFn: () => api.get('/api/v1/bi/alerts').then((r) => r.data),
  });
}

export function useCreateAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      data_config: unknown;
      condition: string;
      threshold: number;
      notify_channel?: string;
      check_interval?: string;
    }) => api.post('/api/v1/bi/alerts', body).then((r) => r.data as DataAlert),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'alerts'] }),
  });
}

export function useUpdateAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Partial<DataAlert>) =>
      api.put(`/api/v1/bi/alerts/${id}`, body).then((r) => r.data as DataAlert),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'alerts'] }),
  });
}

export function useDeleteAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/v1/bi/alerts/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bi', 'alerts'] }),
  });
}
